'use client';

import { createFormHook } from '@tanstack/react-form';
import { useAction } from 'next-safe-action/hooks';
import { useState } from 'react';
import { toast } from 'sonner';
import { cn } from '../../../lib';
import { fieldContext, formContext } from '../../form/form-context';
import { FormSubmitButton } from '../../form/form-submit-button';
import { FormTextField } from '../../form/form-textfield';
import { handlePasswordManagerInteraction } from '../../form/form-utilts';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogTrigger,
} from '../../responsive-dialog';
import { backInStockAction } from './actions/product-details-back-in-stock-action';
import {
  type BackInStockValidatorProps,
  backInStockValidator,
} from './product-details-schema';

export function ProductDetailsBackInStockForm({
  variantGid,
  titleClassName,
  descriptionClassName,
  triggerButtonClassNames,
  submitButtonClassNames,
}: {
  variantGid: string;
  titleClassName: string;
  descriptionClassName: string;
  triggerButtonClassNames: string;
  submitButtonClassNames: string;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const { execute, isExecuting } = useAction(backInStockAction, {
    onSuccess({ data }) {
      form.reset();
      if (data?.success) {
        toast.success(data?.success);
      }
      if (data?.error) {
        toast.error(data?.error);
      }
      setIsOpen(false);
    },
    onError(error) {
      if (error.error.serverError) {
        toast.error(error.error.serverError);
      }
      if (error.error.validationErrors) {
        toast.error(error.error.validationErrors._errors);
      }
      setIsOpen(false);
    },
  });

  const { useAppForm } = createFormHook({
    fieldComponents: {
      FormTextField,
    },
    formComponents: {
      FormSubmitButton,
    },
    fieldContext,
    formContext,
  });

  const form = useAppForm({
    defaultValues: {
      email: '',
      variantGid,
    } as BackInStockValidatorProps,
    validators: {
      onSubmit: backInStockValidator,
    },
    onSubmit: ({ value }) => {
      execute(value);
    },
  });

  return (
    <ResponsiveDialog isOpen={isOpen} onOpenChange={setIsOpen}>
      <ResponsiveDialogTrigger asChild>
        <button
          type="button"
          className={cn(
            'col-span-6 col-start-1 max-md:col-span-12',
            triggerButtonClassNames
          )}
        >
          Notify when available
        </button>
      </ResponsiveDialogTrigger>
      <ResponsiveDialogContent
        title="Notify me when available"
        titleClassName={titleClassName}
        description="Register your email address below to receive an email as soon as this product becomes available."
        descriptionClassName={descriptionClassName}
        showDescription={true}
        onInteractOutside={handlePasswordManagerInteraction}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
          className="grid gap-4 p-4 max-w-sm:pb-8"
        >
          <form.AppField
            name="email"
            children={(field) => (
              <field.FormTextField
                label="Email Address"
                type="email"
                placeholder="your@email.com"
              />
            )}
          />
          <form.AppForm>
            <form.FormSubmitButton
              disabled={isExecuting}
              className={submitButtonClassNames}
              isLoading={isExecuting}
            >
              Notify me when available
            </form.FormSubmitButton>
          </form.AppForm>
          <span>We'll notify you when this product arrives.</span>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
